import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Loader, Ellipsis, X, Bot, Send, FileText, UserCircle } from 'lucide-react';
import { fetchUserIssuesDetails, fetchUserIssuesChat, fetchIssueEventLogs, changeIssueStatus } from '../../store/actions';
import { getFullOSName } from '../../utils/misc/system';
import apiClient from '../../gears/apiClient';
import { toastr } from 'react-redux-toastr';

const fetchAiChatMsgSend = async (id, message, setData) => {
  try {
    const response = await apiClient.post(`/disruption-latest-chat/${id}`, { message });
    
    if (response?.messages?.[1]) {
      setData(prevState => ([ ...prevState, response.messages[1]]));
    }
  } catch(e) {
    toastr.error(e?.error || e?.message)
  }
}

const SummaryElement = ({ title, details = '-' }) => {
  return (
    <div className="bg-white rounded-md p-3 border border-gray-200">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="mt-1">{details}</p>
    </div>
  );
}

const AiHeader = ({ loadingChat, allowsTranscript, chatMode, setChatMode, }) => {
  const isTranscriptMode = chatMode === 'transcript';
  const isChatMode = chatMode === 'chat';

  return (
    <h3 className="flex items-center text-lg font-medium text-gray-900 mb-2 gap-2">
      {loadingChat && (<Loader size={24} className="mr-2 animate-spin" />)} 
      {!loadingChat && (<FileText size={24} className="mr-2" />)}
      <span
        className={isChatMode ? "text-indigo-900" : "text-gray-900 cursor-pointer"}
        onClick={() => setChatMode('chat')}
      >
        HubbleIQ AI Chat
      </span>
      {allowsTranscript && (<span
        className={isTranscriptMode ? "text-indigo-900" : "text-gray-900 cursor-pointer"}
        onClick={() => setChatMode('transcript')}
      >
        Transcript
      </span>)}
    </h3>
  )
}

const TranscriptSection = ({ issueData }) => {
  const isAssistantTranscript = (message) => message.speaker === 'Assistant';
  const isUserTranscript = (message) => message.speaker === 'User';

  return (
    <div className="bg-white rounded-md p-4 border border-gray-200 max-h-80 overflow-y-auto">
      {issueData?.transcript?.conversation.map((msg, index) => (
        <div key={index} className={`mb-3 p-3 rounded-lg ${isAssistantTranscript(msg) ? 'bg-gray-100' : 'bg-indigo-50'}`}>
          <div className="flex items-center mb-1">
            {isAssistantTranscript(msg) && (
              <span className="flex items-center gap-1 text-sm font-medium text-indigo-600">
                <Bot className="w-4 h-4" />
                HubbleIQ AI
              </span>
            )}
            {isUserTranscript(msg) && (
              <span className="flex items-center gap-1 text-sm font-medium text-gray-800">
                <UserCircle className="w-4 h-4" />
                User
              </span>
            )}
          </div>
          <p className="text-gray-700">{msg.message}</p>
        </div>
      ))}
    </div>
  );
}

const DialogSection = ({ chatMessages, sendingMessage }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chatMessages, sendingMessage]);

  const isAssistantMessage = (message) => message.role === 'assistant';
  const isUserMessage = (message) => message.role === 'user';

  return (
    <div ref={containerRef} className="bg-white rounded-md p-4 border border-gray-200 max-h-80 overflow-y-auto">
      {chatMessages.map((msg, index) => (
        <div key={index} className={`mb-3 p-3 rounded-lg ${isAssistantMessage(msg) ? 'bg-gray-100' : 'bg-indigo-50'}`}>
          <div className="flex items-center mb-1">
            {isAssistantMessage(msg) && (
              <span className="flex items-center gap-1 text-sm font-medium text-indigo-600">
                <Bot className="w-4 h-4" />
                HubbleIQ AI
              </span>
            )}
            {isUserMessage(msg) && (
              <span className="flex items-center gap-1 text-sm font-medium text-gray-800">
                <UserCircle className="w-4 h-4" />
                User
              </span>
            )}
          </div>
          <p className="text-gray-700">{msg.content}</p>
        </div>
      ))}
      {sendingMessage && (<div className="mb-3 p-3 rounded-lg bg-gray-100">
        <div className="flex items-center mb-1">
          <span className="flex items-center gap-1 text-sm font-medium text-indigo-600">
            <Bot className="w-4 h-4" />
            HubbleIQ AI
          </span>
        </div>
        <p className="text-gray-700"> <Ellipsis className="animate-bounce" /></p>
      </div>)}
    </div>
  );
}

const NewMessageSection = ({ handleSendMessage, newChatMessage, setNewChatMessage, disabled }) => {
  return (
    <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4">
      <div className="flex items-center">
        <input
          type="text"
          value={newChatMessage}
          onChange={(e) => setNewChatMessage(e.target.value)}
          placeholder="Your message..."
          className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          className="ml-2 inline-flex items-center bg-indigo-600 text-white rounded-md p-2 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={disabled}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}

const EventLogTable = ({ logs, tab }) => {
  if (logs.length === 0) {
    return <div className="text-center py-8 text-gray-500">No results found</div>;
  }

  const getColumns = () => {
  switch (tab) {
    case 'network':
      return [
        { Header: 'Type', accessor: 'type' },
        { Header: 'Start', accessor: 'start' },
        { Header: 'End', accessor: 'end' },
        { Header: 'Duration', accessor: 'duration' },
        { Header: 'Service Provider', accessor: 'serviceProvider' },
        { Header: 'Created at', accessor: 'createdAt' },
      ];

    case 'device':
      return [
        { Header: 'Cpu Utilization', accessor: 'cpuUtilization' },
        { Header: 'Memory Utilization', accessor: 'memoryUtilization' },
        { Header: 'Battery Health', accessor: 'batteryHealth' },
        { Header: 'Created at', accessor: 'createdAt' },
      ];

    case 'applications':
      return [
        { Header: 'Name', accessor: 'name' },
        { Header: 'Url', accessor: 'url' },
        { Header: 'Reachable', accessor: 'reachable' },
        { Header: 'Latency Response', accessor: 'latencyResponse' },
        { Header: 'Average Page Load Time', accessor: 'averagePageLoadTime' },
        { Header: 'Created at', accessor: 'createdAt' },
      ];

    case 'userActivity':
      return [
        { Header: 'Test Result', accessor: 'testResult' },
        { Header: 'IP', accessor: 'ip' },
        { Header: 'Service Provider', accessor: 'serviceProvider' },
        { Header: 'Download', accessor: 'download' },
        { Header: 'Upload', accessor: 'upload' },
        { Header: 'Status', accessor: 'statusDescription' },
        { Header: 'Created at', accessor: 'createdAt' },
      ];

    default:
      return [];
  }
};


  const columns = getColumns();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.accessor}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {column.Header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {logs.map((log, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column.accessor} className="px-4 py-2 whitespace-nowrap">
                  {log[column.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const IssueSlideout = ({ issueId, isOpen, onClose }) => {
  const dispatch = useDispatch();

  const { data: issueData, loading, chatData, loadingChat } = useSelector((state) => state.UserIssuesDetails);
  const { data: eventData, loading: eventsLoading } = useSelector((state) => state.IssuesEventLogs);

  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [activeTab, setActiveTab] = useState('network');

  const [chatMode, setChatMode] = useState('chat');
  const [sendingMessage, setSendingMessage] = useState(false);
  const allowsTranscript = !!issueData?.transcript;
  const isTranscriptMode = chatMode === 'transcript';
  const isChatMode = chatMode === 'chat';

  const firstCityAndCountry = (issueData && issueData?.location?.length > 0) && `${issueData?.location[0].city || '-'}, ${issueData?.location[0]?.country || '-'}`;

  useEffect(() => {
    if (!issueId) { return; }

    dispatch(fetchUserIssuesDetails(issueId));
    dispatch(fetchUserIssuesChat(issueId));
    dispatch(fetchIssueEventLogs(issueId));
  }, [issueId]);

  useEffect(() => {
    if (chatData?.messages && chatData?.messages?.length > 0) {
      setChatMessages(chatData?.messages);
    }
  }, [chatData]);

  const markAsCompleted = async () => {
    if (!issueId) {
      return;
    }

    try {
      dispatch(changeIssueStatus(issueId, 'completed'))
    } catch (err) {
      console.log(err);
    } finally {
      issueData.status = 'Completed';
    }
  }

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (newChatMessage.trim() === '') {
      return;
    }

    const copyInputMsg = newChatMessage;
    setNewChatMessage('');

    setChatMessages(prevMessages => [
      ...prevMessages,
      { role: 'user', content: copyInputMsg }
    ]);

		try {
      setSendingMessage(true);
      await fetchAiChatMsgSend(issueId, copyInputMsg, setChatMessages);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
	};

  const getLogs = (type) => {
    switch (type) {
      case 'network':
        return eventData?.networkIssues;
      
      case 'device':
        return eventData?.deviceActivities;

      case 'applications':
        return eventData?.appActivities;

      case 'userActivity':
        return eventData?.userActivities;

      default:
        return [];
    }
  }

  return (
    <div className={`fixed top-[40px] bottom-0 right-0 w-full md:w-2/3 lg:w-1/2 xl:w-2/5 bg-white shadow-lg transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} transition-transform duration-300 ease-in-out overflow-y-auto z-30`}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            {loading && (<Loader className="animate-spin h-6 w-6 text-gray-500" />)}
            {!loading && (<div>
              <h2 className="text-xl font-bold text-gray-900">{issueData?.category} Issue: {issueData?.type}</h2>
              <p className="text-sm text-gray-500">ID: {issueId}</p>
            </div>)}
          </div>
          <div className="flex items-center space-x-2">
            {(!loading && issueData?.status !== 'Completed' && issueData?.userActive) && (
              <button
                onClick={() => markAsCompleted()}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Check className="mr-1.5 h-4 w-4" />
                Mark Complete
              </button>
            )}
            {(!loading && issueData?.status === 'Completed' && issueData?.userActive) && (
              <button
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600"
                disabled
              >
                <Check className="mr-1.5 h-4 w-4" />
                Completed
              </button>
            )}            
            <button
              onClick={onClose}
              className="inline-flex items-center p-2 border border-transparent rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 py-4 space-y-6">
          {/* Summary Info */}
          <div>
            <h3 className="flex gap-2 text-lg font-medium text-gray-900 mb-2">
              {loading && (<Loader size={24} className="animate-spin"/>)}
              Summary
            </h3>
            {!loading && (<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SummaryElement title="Reported Date" details={issueData?.createdAt} />
              <SummaryElement title="Location" details={firstCityAndCountry} />
              {issueData?.app && (<SummaryElement title="App" details={issueData?.app} />)}
              {issueData?.userDevice?.serialNumber && (<SummaryElement title="Serial Number" details={issueData?.userDevice?.serialNumber} />)}
              {issueData?.userDevice?.operatingSystem && (<SummaryElement title="Operating System" details={getFullOSName(issueData?.userDevice?.operatingSystem)} />)}
            </div>)}
          </div>

          {/* Reported Problem */}
          {issueData.description && (<div>
            <h3 className="flex gap-2 text-lg font-medium text-gray-900 mb-2">
              {loading && (<Loader size={24} className="animate-spin"/>)}
              Reported Problem
            </h3>
            {!loading && (<div className="bg-white rounded-md p-4 border border-gray-200">
              <p className="text-gray-700">{issueData.description}</p>
            </div>)}
          </div>)}

          {/* Ai Chat */}
          <div>
            <AiHeader
              loadingChat={loadingChat}
              allowsTranscript={allowsTranscript}
              chatMode={chatMode}
              setChatMode={setChatMode}
            />
            {!loadingChat && isTranscriptMode && (<TranscriptSection issueData={issueData}/>)}
            {!loadingChat && isChatMode && (<DialogSection chatMessages={chatMessages} sendingMessage={sendingMessage} />)}
            {!loadingChat && isChatMode && (
              <NewMessageSection
                handleSendMessage={handleSendMessage}
                newChatMessage={newChatMessage}
                setNewChatMessage={setNewChatMessage}
                disabled={sendingMessage}
              />
            )}
          </div>

          {/* Event Logs */}
          <div>
            <h3 className="flex gap-2 text-lg font-medium text-gray-900 mb-2">
              {eventsLoading && (<Loader size={24} className="animate-spin"/>)}
              Event Logs
            </h3>
            {!eventsLoading && (<div className="border border-gray-200 rounded-md">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {['network', 'device', 'applications', 'userActivity'].map((tab) => {
                    const label = tab === 'userActivity' ? 'User Activity' : 
                                tab.charAt(0).toUpperCase() + tab.slice(1);
                    return (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 text-sm font-medium ${
                          activeTab === tab
                            ? 'border-b-2 border-indigo-500 text-indigo-600'
                            : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </nav>
              </div>
                <div className="p-4">
                  <EventLogTable tab={activeTab} logs={getLogs(activeTab) || []} />
                </div>
              </div>)}
            </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueSlideout;
